// src/admin/StatisticsPage.js

import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler, ArcElement, RadialLinearScale } from 'chart.js';
import { Bar, Line, Doughnut, Pie, Radar } from 'react-chartjs-2';
import { CurrencyPoundIcon, ShoppingCartIcon, ChartBarIcon, TagIcon, UserGroupIcon, CakeIcon, ClockIcon, BookOpenIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

// Register all necessary Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler, ArcElement, RadialLinearScale);

// API Info
const API_URL = 'https://littlehibba.com/api.php';
const API_KEY = 'orichalcos';

// API fetching function (Updated to send period)
const fetchData = async (action, params = {}) => {
    const url = new URL(API_URL);
    url.searchParams.append('action', action);
    Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
    });

    const response = await fetch(url.toString(), { headers: { 'X-API-Key': API_KEY } });
    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
    }
    return await response.json();
};

// Helper function for GBP currency format
const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);

// --- HELPER COMPONENTS ---

const StatCard = ({ title, value, icon }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm d-flex align-items-center h-100">
        <div className="p-3 rounded-circle bg-light me-4">{icon}</div>
        <div>
            <div className="text-muted">{title}</div>
            <div className="fs-4 fw-bold">{value}</div>
        </div>
    </div>
);

const TopList = ({ title, data, valueKey, labelKey, icon }) => (
    <div className="card h-100 shadow-sm">
        <div className="card-body">
            <h5 className="card-title d-flex align-items-center mb-3">
                {React.cloneElement(icon, { className: "list-icon text-primary" })}
                <span className="ms-2">{title}</span>
            </h5>
            <ul className="list-group list-group-flush">
                {data && data.length > 0 ? data.map((item, index) => (
                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center px-0">
                        <span className="text-truncate pe-2">{item[labelKey]}</span>
                        <span className="badge bg-primary rounded-pill">{item[valueKey]}</span>
                    </li>
                )) : <p className="text-muted">No data available.</p>}
            </ul>
        </div>
    </div>
);

const CustomerCard = ({ name, email, orderCount, totalSpent }) => (
    <div className="customer-card">
        <div className="customer-card-header">
            <div className="customer-avatar">{name ? name.charAt(0).toUpperCase() : '?'}</div>
            <div className="customer-info">
                <p className="name" title={name}>{name || 'N/A'}</p>
                <p className="email" title={email}>{email}</p>
            </div>
        </div>
        <div className="customer-stats">
            <div className="customer-stat">
                <div className="label">Orders</div>
                <div className="value">{orderCount}</div>
            </div>
            <div className="customer-stat">
                <div className="label">Total Spent</div>
                <div className="value">{formatCurrency(totalSpent)}</div>
            </div>
        </div>
    </div>
);

const ProfitCard = ({ data, title }) => {
    if (!data) return null;
    return (
        <div className="profit-card">
            <div className="text-center">
                {/* UPDATED: Dynamic Title */}
                <h4 className="text-white fw-light">Net Profit ({title})</h4>
                <p className="value">{formatCurrency(data.netProfit)}</p>
            </div>
            <div className="profit-breakdown">
                <div className="breakdown-item">
                    <div className="label">Gross Revenue</div>
                    <div className="amount">{formatCurrency(data.grossRevenue)}</div>
                </div>
                <div className="breakdown-item">
                    <div className="label">Print & Fulfilment Costs</div>
                    <div className="amount negative">- {formatCurrency(data.totalPrintCost)}</div>
                </div>
                <div className="breakdown-item">
                    <div className="label">Recorded Shipping Costs</div>
                    <div className="amount negative">- {formatCurrency(data.totalShippingCost)}</div>
                </div>
            </div>
        </div>
    );
};


// --- MAIN PAGE COMPONENT ---

const StatisticsPage = () => {
    const [stats, setStats] = useState(null);
    // const [weeklyData, setWeeklyData] = useState(null); // REMOVED
    const [monthlyCharts, setMonthlyCharts] = useState(null);
    const [visuals, setVisuals] = useState(null);
    const [profitData, setProfitData] = useState(null);
    // const [profitTrend, setProfitTrend] = useState(null); // REMOVED
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // UPDATED: Filter state, default to 'monthly'
    const [period, setPeriod] = useState('monthly'); 

    useEffect(() => {
        const loadAllData = async (filterPeriod) => {
            try {
                setLoading(true);
                setError(null);

                // Parameters object to send with API request
                const params = { period: filterPeriod };

                // UPDATED: Removed getWeeklyChartData and getProfitTrend
                const [advancedStats, monthlyChartData, visualsData, profitStats] = await Promise.all([
                    fetchData('getAdvancedStats', params),
                    fetchData('getChartData', params),
                    fetchData('getDashboardVisuals', params),
                    fetchData('getProfitStats', params)
                ]);
                
                setStats(advancedStats);
                setMonthlyCharts(monthlyChartData);
                setVisuals(visualsData);
                setProfitData(profitStats);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        // Re-run when 'period' state changes
        loadAllData(period);
    }, [period]);

    // UPDATED: Helper variable for dynamic titles
    const periodTitle = period === 'monthly' ? 'Monthly' : (period === 'yearly' ? 'Yearly' : 'All Time');

    // --- CHART SETTINGS ---
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: { grid: { display: false } } },
    };

    const doughnutPieOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { padding: 15 } } }
    };
    
    const beautifulColors = ['#5E81AC', '#88C0D0', '#81A1C1', '#B48EAD', '#A3BE8C', '#BF616A'];

    // This is global, not filtered
    const orderStatusData = {
        labels: visuals?.orderStatus?.labels || [],
        datasets: [{ data: visuals?.orderStatus?.data || [], backgroundColor: beautifulColors, borderWidth: 0 }]
    };

    // This is global, not filtered
    const coverTypeData = {
        labels: visuals?.coverTypes?.labels || [],
        datasets: [{ data: visuals?.coverTypes?.data || [], backgroundColor: ['#D08770', '#EBCB8B', '#BF616A'], borderWidth: 0 }]
    };
    
    // This chart IS filtered
    const salesByHourData = {
        labels: visuals?.salesByHour?.labels || [],
        datasets: [{
            label: 'Number of Orders',
            data: visuals?.salesByHour?.data || [],
            backgroundColor: 'rgba(179, 142, 173, 0.2)',
            borderColor: 'rgba(179, 142, 173, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(179, 142, 173, 1)',
        }]
    };

    // This chart data is now dynamic based on period
    const salesData = {
        labels: monthlyCharts?.monthlySalesQuantity?.labels || [],
        datasets: [{
            label: 'Sales (Units)', data: monthlyCharts?.monthlySalesQuantity?.data || [],
            backgroundColor: '#50E3C2', borderRadius: 5,
        }],
    };

    // This chart data is now dynamic based on period
    const earningsData = {
        labels: monthlyCharts?.monthlyEarnings?.labels || [],
        datasets: [{
            label: 'Revenue', data: monthlyCharts?.monthlyEarnings?.data || [],
            borderColor: '#4A90E2', fill: true, tension: 0.4,
            backgroundColor: (context) => {
                if (!context.chart.chartArea) return;
                const {ctx, chartArea: {top, bottom}} = context.chart;
                const gradient = ctx.createLinearGradient(0, top, 0, bottom);
                gradient.addColorStop(0, 'rgba(74, 144, 226, 0.5)');
                gradient.addColorStop(1, 'rgba(74, 144, 226, 0)');
                return gradient;
            },
        }],
    };
    

    return (
        <div className="stats-dashboard">
            <h1 className="fw-bold mb-4">Sales Dashboard</h1>

            {/* --- UPDATED FILTER BUTTONS (CENTERED) --- */}
            <div className="d-flex justify-content-center mb-4">
                <div className="btn-group" role="group" aria-label="Time Filter">
                    <button 
                        type="button" 
                        className={`btn ${period === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`} 
                        onClick={() => setPeriod('monthly')}
                    >
                        Monthly
                    </button>
                    <button 
                        type="button" 
                        className={`btn ${period === 'yearly' ? 'btn-primary' : 'btn-outline-primary'}`} 
                        onClick={() => setPeriod('yearly')}
                    >
                        Yearly
                    </button>
                    <button 
                        type="button" 
                        className={`btn ${period === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} 
                        onClick={() => setPeriod('all')}
                    >
                        All Time
                    </button>
                </div>
            </div>
            {/* --- END FILTER BUTTONS --- */}
            

            {/* Loading and Error states */}
            {loading ? (
                <div className="text-center p-5"><strong>Loading Dashboard...</strong></div>
            ) : error ? (
                <div className="alert alert-danger mx-4"><strong>Error:</strong> {error}</div>
            ) : (
                // Main content
                <>
                    {/* UPDATED: Title is dynamic */}
                    <ProfitCard data={profitData} title={periodTitle} />
                    
                    <div className="row g-4 mb-4">
                        <div className="col-xl-3 col-md-6"><StatCard title="Total Revenue" value={formatCurrency(stats?.totalRevenue)} icon={<CurrencyPoundIcon className="w-6 h-6 text-success"/>} /></div>
                        <div className="col-xl-3 col-md-6"><StatCard title="Avg. Order Value" value={formatCurrency(stats?.averageOrderValue)} icon={<ChartBarIcon className="w-6 h-6 text-primary"/>} /></div>
                        <div className="col-xl-3 col-md-6"><StatCard title="Total Orders" value={stats?.totalOrders.toLocaleString('en-US')} icon={<ShoppingCartIcon className="w-6 h-6 text-info"/>} /></div>
                        <div className="col-xl-3 col-md-6"><StatCard title="Products Sold" value={stats?.totalProductsSold.toLocaleString('en-US')} icon={<TagIcon className="w-6 h-6 text-warning"/>} /></div>
                    </div>
                    
                    {/* --- UPDATED: Main Overviews Section --- */}
                    <h3 className="fw-bold mb-3 mt-5">{periodTitle} Overviews</h3>
                    <div className="row g-4 mb-5">
                        <div className="col-lg-6">
                            <div className="card h-100 shadow-sm">
                                <div className="card-body d-flex flex-column">
                                    <h5 className="card-title">Sales (Units)</h5>
                                    <div className="flex-grow-1" style={{ minHeight: '300px' }}><Bar options={{ ...chartOptions, scales: { ...chartOptions.scales, y: { ticks: { callback: (value) => value } } } }} data={salesData} /></div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="card h-100 shadow-sm">
                                <div className="card-body d-flex flex-column">
                                    <h5 className="card-title">Revenue (£)</h5>
                                    <div className="flex-grow-1" style={{ minHeight: '300px' }}><Line options={{ ...chartOptions, scales: { ...chartOptions.scales, y: { ticks: { callback: (value) => `£${value}` } } } }} data={earningsData} /></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* --- END: Main Overviews Section --- */}

                    {/* --- UPDATED: Details Section --- */}
                    <h3 className="fw-bold mb-3 mt-5">{periodTitle} Details</h3>
                    <div className="row g-4 mb-5">
                        <div className="col-lg-4">
                            <TopList title="Best-Selling Products" data={stats?.bestSellers} valueKey="total_sold" labelKey="product_name" icon={<CakeIcon />} />
                        </div>
                        <div className="col-lg-4">
                            <div className="card h-100 shadow-sm">
                                <div className="card-body d-flex flex-column">
                                    <h5 className="card-title d-flex align-items-center"><ClockIcon className="list-icon text-danger me-2"/>Sales by Hour of Day</h5>
                                    <div className="flex-grow-1" style={{ minHeight: '250px' }}><Radar data={salesByHourData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                             {/* Placeholder or another chart. Let's move Top Countries here */}
                             <TopList title="Top Countries" data={stats?.topCountries} valueKey="order_count" labelKey="country" icon={<ChartBarIcon />} />
                        </div>
                    </div>
                    {/* --- END: Details Section --- */}


                    {/* UPDATED: Dynamic Title */}
                    <h3 className="fw-bold mb-3 mt-5 d-flex align-items-center"><UserGroupIcon className="list-icon text-dark me-2" />{periodTitle} Top Customers</h3>
                    <div className="row g-4 mb-5">
                        {stats?.topCustomers?.length > 0 ? stats.topCustomers.map((customer, index) => (
                            <div className="col-lg col-md-6" key={`top-g-${index}`}>
                                <CustomerCard name={customer.parent_name} email={customer.mail_adress} orderCount={customer.order_count} totalSpent={customer.total_spent} />
                            </div>
                        )) : <div className="col-12"><p className="text-muted">Not enough customer data to display for this period.</p></div>}
                    </div>
                    
                    {/* --- NEW: Global Statistics Section (Not filtered by date) --- */}
                    <h3 className="fw-bold mb-3 mt-5">Global Statistics (All Time)</h3>
                    <div className="row g-4 mb-5">
                        <div className="col-lg-4">
                            <div className="card h-100 shadow-sm">
                                <div className="card-body d-flex flex-column">
                                    <h5 className="card-title d-flex align-items-center"><CheckCircleIcon className="list-icon text-info me-2"/>Order Status Distribution</h5>
                                    <div className="flex-grow-1" style={{ minHeight: '250px' }}><Doughnut data={orderStatusData} options={doughnutPieOptions} /></div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="card h-100 shadow-sm">
                                <div className="card-body d-flex flex-column">
                                    <h5 className="card-title d-flex align-items-center"><BookOpenIcon className="list-icon text-warning me-2"/>Sales by Cover Type</h5>
                                    <div className="flex-grow-1" style={{ minHeight: '250px' }}><Pie data={coverTypeData} options={doughnutPieOptions} /></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* --- END: Global Statistics Section --- */}
                </>
            )}
        </div>
    );
};

export default StatisticsPage;